import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import {
  WebAppCompositionSocialUploadItemDto,
  WebAppFragmentSocialUploadItemDto,
} from '@mi-front/api-v2';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { IconButton } from '@mui/material';
import { useEffect, useState } from 'react';
import { UploadCardActionsMenu } from './UploadCardActionsMenu';
import { useUploadCardMutations } from './useUploadCardMutations';

export type UploadCardDto =
  | WebAppCompositionSocialUploadItemDto
  | WebAppFragmentSocialUploadItemDto;

export interface UseUploadCardActionsParams {
  upload: UploadCardDto;
  onRetry: () => void;
  /** Runs after delete succeeds; perform all query invalidations. */
  onDeleteSuccess: (uploadId: string) => Promise<void>;
  /** Runs after promote succeeds; perform all query invalidations. */
  onPromoteSuccess: (uploadId: string) => Promise<void>;
}

/**
 * Overflow menu and delete dialog for a social upload card (fragment or composition list).
 * Callers supply cache invalidation and retry navigation.
 */
export function useUploadCardActions({
  upload,
  onRetry,
  onDeleteSuccess,
  onPromoteSuccess,
}: UseUploadCardActionsParams) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const open = Boolean(anchorEl);

  const { deleteMutation, promoteMutation } = useUploadCardMutations({
    onDeleteSuccess,
    onPromoteSuccess,
  });

  const platform = socialPlatforms.get(upload.platform);
  const status = upload.status;
  const isCompleted = status === 'COMPLETED';
  const hasPublicUrl = Boolean(upload.platformFeatures?.publicUrl);
  const isWaitingPublicLink = isCompleted && !hasPublicUrl;
  const isInProgress =
    status === 'WAITING' || status === 'WAITING_CHILDREN' || status === 'IN_PROGRESS';

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isWaitingPublicLink || isInProgress) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (isWaitingPublicLink || isInProgress) {
      setAnchorEl(null);
    }
  }, [isInProgress, isWaitingPublicLink]);

  const handleDelete = async () => {
    await deleteMutation.mutateAsync({ id: upload.id });
  };

  const handlePromote = async () => {
    await promoteMutation.mutateAsync({ id: upload.id });
  };

  const handleRetry = () => {
    handleClose();
    onRetry();
  };

  /** Borderless icon-only overflow control — independent from the primary "view video" CTA. */
  const trigger =
    isWaitingPublicLink || isInProgress ? null : (
      <IconButton
        size="small"
        onClick={handleOpen}
        sx={{
          border: 'none',
          color: 'text.secondary',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <MoreHorizIcon fontSize="small" />
      </IconButton>
    );

  const menuAndDialog =
    isWaitingPublicLink || isInProgress ? null : (
      <UploadCardActionsMenu
        upload={upload}
        platform={platform}
        anchorEl={anchorEl}
        open={open}
        deleteDialogOpen={deleteDialogOpen}
        isDeleting={deleteMutation.isPending}
        isPromoting={promoteMutation.isPending}
        onClose={handleClose}
        onDeleteDialogOpen={() => setDeleteDialogOpen(true)}
        onDeleteDialogClose={() => setDeleteDialogOpen(false)}
        onDelete={handleDelete}
        onPromote={handlePromote}
        onRetry={handleRetry}
      />
    );

  return { trigger, menuAndDialog };
}
